import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Auth } from 'aws-amplify';
import { AuthState, User } from '../types';
import { UserType } from '../constants/userTypes';
import { directAuth, mapAuthErrorToCode } from '../services/directAuth';
import apolloClient from '../graphql/apollo-client';
import toast from 'react-hot-toast';
import i18next from 'i18next';

/**
 * Resolve a localized message for an auth failure. Prefers the Cognito error
 * code (e.g. `NotAuthorizedException`) against `auth:authErrors.<CODE>`, and
 * falls back to a provided fallback i18n key when no mapping exists.
 *
 * We deliberately do NOT echo `error.message`, since Cognito messages are
 * English-only and would bypass the active locale.
 */
function localizeAuthError(
  errorCodeOrError: string | { code?: string; name?: string; message?: string } | null | undefined,
  fallbackKey: string,
): string {
  const code =
    typeof errorCodeOrError === 'string'
      ? errorCodeOrError
      : mapAuthErrorToCode(errorCodeOrError);
  const fallback = i18next.t(fallbackKey);
  if (!code) return fallback;
  return i18next.t(`auth:authErrors.${code}`, { defaultValue: fallback });
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      setAuthenticatedUser: (user: User) => {
        set({
          user,
          isAuthenticated: true,
        });
      },

      updateUser: (user: User) => {
        set({ user });
      },

      updateUserLanguage: (language: string) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: {
              ...currentUser,
              languagePreference: language,
            },
          });
        }
      },

      login: async (email: string, password: string) => {
        try {
          console.log('🔐 Starting login process...', { email });

          // Use Amplify signIn directly to ensure proper session establishment

          const result = await Auth.signIn({
            username: email,
            password,
          });

          if (result) {
            console.log('✅ Login successful, setting user state');

            // Get current user and session from Amplify
            const [currentUser, session] = await Promise.all([
              Auth.currentAuthenticatedUser(),
              Auth.currentSession()
            ]);

            // Get user data from our backend
            console.log('🔍 Fetching user data from backend...');
            let userData = null;
            try {
              const userResponse = await directAuth.getCurrentUser();
              if (userResponse.success && userResponse.data) {
                userData = userResponse.data;
                console.log('✅ User data from directAuth:', userData);
              } else {
                console.log('⚠️ DirectAuth failed:', userResponse.error);
              }
            } catch (backendError) {
              console.log('⚠️ DirectAuth exception:', backendError);
            }

            if (userData) {
              set({
                user: userData,
                token: session.getIdToken().getJwtToken(), // Use ID token for GraphQL auth
                isAuthenticated: true,
              });
            } else {
              throw new Error('Failed to get user data from Cognito');
            }

            toast.success(i18next.t('auth:toast.loginSuccess'));
          } else {
            throw new Error('Login not completed');
          }
        } catch (error: any) {
          console.error('❌ Login error:', error);
          toast.error(localizeAuthError(error, 'auth:toast.loginFailed'));
          throw error;
        }
      },

      register: async (
        email: string,
        password: string,
        name: string,
        userType: UserType,
        tosAcceptedVersion: string,
        tosAcceptedAt: number,
        marketingConsent?: boolean
      ) => {
        try {
          console.log('📝 Starting registration process...', { email, name, userType, tosAcceptedVersion });

          const response = await directAuth.signUp(
            email,
            password,
            name,
            userType,
            tosAcceptedVersion,
            tosAcceptedAt,
            marketingConsent
          );

          if (response.success) {
            console.log('✅ Registration successful');
            toast.success(i18next.t('auth:toast.registrationSuccess'));
          } else {
            console.error('❌ Registration failed:', response.error);
            throw new Error(response.errorCode || response.error || 'Registration failed');
          }
        } catch (error: any) {
          console.error('❌ Registration error:', error);
          toast.error(localizeAuthError(error, 'auth:toast.registrationFailed'));
          throw error;
        }
      },

      confirmEmail: async (email: string, confirmationCode: string) => {
        try {
          console.log('✉️ Starting email confirmation...', { email });

          const response = await directAuth.confirmEmail(email, confirmationCode);

          if (response.success) {
            console.log('✅ Email confirmation successful');
            toast.success(i18next.t('auth:toast.emailConfirmed'));
          } else {
            console.error('❌ Email confirmation failed:', response.error);
            throw new Error(response.errorCode || response.error || 'Email confirmation failed');
          }
        } catch (error: any) {
          console.error('❌ Email confirmation error:', error);
          toast.error(localizeAuthError(error, 'auth:toast.emailConfirmationFailed'));
          throw error;
        }
      },

      resendVerificationCode: async (email: string) => {
        try {
          console.log('📧 Resending verification code...', { email });

          const response = await directAuth.resendVerificationCode(email);

          if (response.success) {
            console.log('✅ Verification code resent successfully');
            toast.success(i18next.t('auth:codeSent'));
          } else {
            console.error('❌ Failed to resend verification code:', response.error);
            throw new Error(response.errorCode || response.error || 'Failed to resend verification code');
          }
        } catch (error: any) {
          console.error('❌ Resend verification code error:', error);
          toast.error(localizeAuthError(error, 'auth:toast.emailConfirmationFailed'));
          throw error;
        }
      },

      logout: async () => {
        try {
          console.log('🚪 Starting logout process...');

          await directAuth.signOut();

          // Clear Apollo cache to prevent stale permissions on next login
          await apolloClient.clearStore();

          // Clear persisted auth storage
          localStorage.removeItem('auth-storage');

          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });

          console.log('✅ Logout successful');
          toast.success(i18next.t('auth:toast.loggedOut'));
        } catch (error: any) {
          console.error('❌ Logout error:', error);
          // Still clear local state even if Cognito signout fails
          try { await apolloClient.clearStore(); } catch { /* ignore */ }
          localStorage.removeItem('auth-storage');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });
          toast.success(i18next.t('auth:toast.loggedOut'));
        }
      },

      checkAuthState: async () => {
        try {
          console.log('🔍 Checking authentication state...');

          const response = await directAuth.getCurrentUser();

          if (response.success && response.data) {
            console.log('✅ Found authenticated user:', response.data);

            // Get current session for token
            try {
              const session = await Auth.currentSession();
              set({
                user: response.data,
                token: session.getIdToken().getJwtToken(),
                isAuthenticated: true,
              });
            } catch {
              // No Cognito session — check if we have a stored token (Naver OAuth)
              const currentState = get();
              if (currentState.token) {
                console.log('ℹ️ Using persisted token for Naver OAuth user');
                set({
                  user: response.data,
                  isAuthenticated: true,
                });
              } else {
                // No session and no token — not authenticated
                console.log('ℹ️ No Cognito session and no stored token');
                set({ user: null, token: null, isAuthenticated: false });
                return false;
              }
            }

            return true;
          } else {
            // No Cognito user found — try to restore session from localStorage tokens
            try {
              const session = await Auth.currentSession();
              // Session exists but getCurrentUser failed — retry
              const retryResponse = await directAuth.getCurrentUser();
              if (retryResponse.success && retryResponse.data) {
                set({
                  user: retryResponse.data,
                  token: session.getIdToken().getJwtToken(),
                  isAuthenticated: true,
                });
                return true;
              }
            } catch {
              // No session at all
            }

            console.log('ℹ️ No authenticated user found');
            set({ user: null, token: null, isAuthenticated: false });
            return false;
          }
        } catch (error: any) {
          console.error('❌ Auth state check failed:', error);
          set({ user: null, token: null, isAuthenticated: false });
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
